import { useCallback } from "react"
import { supabase } from "../lib/supabase"
import { hasPermission, canAccessPage } from "../lib/roles"
import { useAppStore } from "../store/useAppStore"
import { resolveBusinessId } from "../utils/resolveBusinessId"

export function useUser() {
  const user = useAppStore((state) => state.user)
  const userRole = useAppStore((state) => state.userRole)
  const businessId = useAppStore((state) => state.businessId)
  const loading = useAppStore((state) => state.loading)
  const error = useAppStore((state) => state.error)
  const setUserRole = useAppStore((state) => state.setUserRole)
  const clearAuthSnapshot = useAppStore((state) => state.clearAuthSnapshot)

  const updateUserRole = useCallback((newRole) => {
    setUserRole(newRole)
  }, [setUserRole])

  const logout = useCallback(async () => {
    let timeoutId

    try {
      console.log("[SIGNOUT] START")

      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error("Sign out timed out"))
        }, 10000)
      })

      const signOutPromise = supabase.auth.signOut()

      const { error } = await Promise.race([
        signOutPromise,
        timeoutPromise,
      ])

      console.log("[SIGNOUT] RESPONSE:", error)

      if (error) {
        throw error
      }
    } catch (err) {
      console.error("[SIGNOUT] ERROR:", err)
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }

      clearAuthSnapshot()
      console.log("[SIGNOUT] CLEARED STORE")

      if (typeof window !== "undefined") {
        window.location.replace("/")
      }
    }
  }, [clearAuthSnapshot])

  return {
    user,
    userRole,
    businessId,
    loading,
    error,
    updateUserRole,
    logout,
  }
}

export function useResolvedBusinessId() {
  const businessId = useAppStore((state) => resolveBusinessId(state))
  const loading = useAppStore((state) => state.loading)
  const initialized = useAppStore((state) => state.initialized)
  const authLoading = loading && !initialized

  return {
    businessId,
    authLoading,
    ready: initialized && !authLoading && Boolean(businessId),
  }
}

export function useCurrentBusiness() {
  const { businessId, authLoading, ready } = useResolvedBusinessId()
  return { businessId, loading: authLoading, ready }
}

export function usePermission(permission) {
  const { userRole } = useUser()
  return userRole && hasPermission(userRole, permission)
}

export function useCanAccessPage(pathname) {
  const { userRole } = useUser()
  return userRole && canAccessPage(userRole, pathname)
}

export function useIsRole(role) {
  const { userRole } = useUser()
  return userRole === role
}

export function useIsOwnerOrManager() {
  const { userRole } = useUser()
  return userRole === "owner" || userRole === "manager"
}

export function useIsOwner() {
  const { userRole } = useUser()
  return userRole === "owner"
}

export function useIsManager() {
  const { userRole } = useUser()
  return userRole === "manager"
}

export function useIsCashier() {
  const { userRole } = useUser()
  return userRole === "cashier"
}
